/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormControl, Grid, Container, Divider } from '@material-ui/core';
import { Dropdown, ComponentController, Footer, Banner, FormButton, BannerType } from 'components/common';
import { PayeeFormNames, PayeeTypes } from './Helpers';
import CompanyPayee from './CompanyPayee';
import IndividualPayee from './IndividualPayee';
import useCreatePayeeStyles from './CreatePayee.styles';
import { submitPayee } from './CreatePayee.api';
import { fetchCurrentUser } from 'Api';

const CreatePayee = ({ setLoading, recordId, redirectUri }) => {
  let validationRef = useRef(null);
  const classes = useCreatePayeeStyles();
  const initialPayeeDetails = {
    payeeType: '',
    transactionId: recordId,
    countryOfIncorporationId: null,
    countryOfIncorporationIso2: '',
    countryOfIncorporation: '',
    countryOfResidencyId: null,
    countryOfResidencyIso2: '',
    countryOfResidency: '',
    dateOfBirth: null,
    address: '',
    postCode: '',
    payee: '',
    payeeDetails: null,
    subdivisions: [],
    stateOfIncorporation: '',
    stateOfIncorporationCode: '',
    stateOfResidency: '',
    stateOfResidencyCode: '',
    orbisId: '',
    hamiltonId: '',
    source: '',
    givenName: '',
    familyName: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    userName: '',
    userEmail: '',
  };

  const initialForceClearDatasourceState = { '': false };
  const [payeeDetailsState, setPayeeDetailsState] = useState(initialPayeeDetails);
  const [forceClearDatasourceState, setForceClearDatasourceState] = useState(initialForceClearDatasourceState);
  const [abortController, setAbortController] = useState(new AbortController());
  const [bannerState, setBannerState] = useState({});

  const {
    control,
    setValue,
    trigger,
    reset,
    resetField,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...payeeDetailsState },
  });

  useEffect(() => {
    validationRef.current = async () => {
      let isValid = await trigger();
      if (!isValid) {
        setBannerState({
          show: true,
          type: BannerType.warning,
          message: "Form contains validation errors. Please provide valid information."
        });
      }

      return isValid;
    };
  }, [errors, setBannerState, trigger, validationRef]);

  useEffect(() => {
    const initializeForm = async () => {
      setLoading(true);
      var user = await fetchCurrentUser();
      setPayeeDetailsState((previous) => {
        return {
          ...previous,
          userName: user?.displayName,
          userEmail: user?.mail
        };
      });

      setLoading(false);
    }

    initializeForm();
  }, [setLoading, setPayeeDetailsState, fetchCurrentUser]);

  const getRequiredError = (name, label) => {
    return (
      errors[name]?.type === 'required' && (
        <span role="alert">"{label}" is required</span>
      )
    );
  };

  const renderPayeeTypeFields = () => {
    const payeeType = payeeDetailsState.payeeType;
    const payeeProps = {
      payeeDetailsState: payeeDetailsState,
      setPayeeDetailsState: setPayeeDetailsState,
      abortController: abortController,
      setAbortController: setAbortController,
      control: control,
      forceClearDatasourceState: forceClearDatasourceState,
      setForceClearDatasourceState: setForceClearDatasourceState,
      errors: errors,
      getRequiredError: getRequiredError,
      setBannerState: setBannerState,
      setValue: setValue,
      reset: reset,
      resetField: resetField,
      setLoading: setLoading,
    };
    if (!payeeType) {
      return <></>;
    }
    else if (payeeType == PayeeTypes.company) {
      return <CompanyPayee {...payeeProps} />;
    } else if (payeeType == PayeeTypes.individual) {
      return <IndividualPayee {...payeeProps} />;
    }
  }

  const onPayeeTypeChange = useCallback(
    (event) => {
      let payeeType = event.target?.value;

      reset({
        payeeType: payeeType,
        countryOfIncorporation: '',
        countryOfResidency: '',
        dateOfBirth: null,
        address: '',
        postCode: '',
        payee: '',
        stateOfIncorporation: '',
        stateOfResidency: ''
      })

      setForceClearDatasourceState({
        "countryOfIncorporation": true,
        "countryOfResidency": true,
        "assured": true,
        "stateOfIncorporation": true,
        "stateOfResidency": true
      });

      const { userName, userEmail, ...restInitial } = initialPayeeDetails;

      setPayeeDetailsState((previous) => {
        return {
          ...previous,
          ...restInitial,
          payeeType,
          userName: previous.userName,
          userEmail: previous.userEmail,
        };
      });
    }, [setPayeeDetailsState, setForceClearDatasourceState, setValue]);

  const renderPayeeType = () => {
    const name = "payeeType";
    const payeeTypeOptions = Object.values(PayeeTypes);
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          required
          render={({ field: { name, onBlur, onChange } }) => (
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                disabled={false}
                label={PayeeFormNames[name]}
                value={payeeDetailsState[name]}
                data={payeeTypeOptions}
                onChange={(e) => {
                  onPayeeTypeChange(e);
                  onChange(e);
                }}
                onBlur={onBlur}
                errorText={getRequiredError(name, PayeeFormNames[name])}
                shouldSetDefaultValue={true}
              />
            </FormControl>
          )}
        />
      </Grid>
    );
  };

  const clearForm = useCallback(() => {
    reset({
      payeeType: '',
      countryOfIncorporation: '',
      countryOfResidency: '',
      dateOfBirth: null,
      address: '',
      postCode: '',
      payee: '',
      stateOfIncorporation: '',
      stateOfResidency: ''
    })

    const { userName, userEmail, ...restInitial } = initialPayeeDetails;
    setPayeeDetailsState((previous) => {
      return {
        ...previous,
        ...restInitial,
        userName: previous.userName,
        userEmail: previous.userEmail,
      };
    });

    setForceClearDatasourceState({ ...initialForceClearDatasourceState });
  }, [reset, setPayeeDetailsState, setForceClearDatasourceState]);

  const onSubmit = useCallback(async () => {
    let validateAction = validationRef.current;
    if (validateAction) {
      let validationResult = await validateAction();
      if (validationResult) {
        setLoading(true);
        let response = await submitPayee(payeeDetailsState);
        if (response.success) {
          clearForm();

          setBannerState({
            type: BannerType.success,
            message: "Payee Creation is successfully started.",
            show: true
          });
          setLoading(false);
          setTimeout(() => {
            window.location.replace(redirectUri);
          }, 3000);
        } else {
          setBannerState({
            type: BannerType.error,
            message: response.errorMessage,
            show: true,
            expose: true
          });
          setLoading(false);
        } 
      }
    }
  }, [payeeDetailsState, redirectUri]);

  return (
    <Container>
      <Banner
        type={bannerState.type}
        message={bannerState.message}
        show={bannerState.show}
        showBanner={show => setBannerState({
          ...bannerState,
          show: show
        })}
      />
      <Grid container className={classes.instructions} direction="row" justifyContent="center" alignItems="flex-start" spacing={4}>
        <Grid item container xs={12} direction="column">
          <form className={classes.fields}>
            <Grid item container direction="column" spacing={2}>
              <h3 className={classes.heading}>Payee Details</h3>
              <Divider className={classes.dividerSpacing} />
              {renderPayeeType()}
              {renderPayeeTypeFields()}
            </Grid>
          </form>
        </Grid>
        <Grid id="portal-root" item container xs={3} direction="column" spacing={4}>
        </Grid>
      </Grid>
      <div className={classes.footer}>
        <FormButton onClick={onSubmit}>Submit</FormButton>
      </div>
      <Footer />
    </Container>
  );
};

export default CreatePayee;